import React, { useState, useEffect, useRef } from "react";
import api from "../services/api";
import { toast } from "react-toastify";
import { MdClose } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";

const LuckyWheel = ({
  isAdminPreview = false,
  previewSlots = [],
  onPreviewClose = null,
}) => {
  const [show, setShow] = useState(isAdminPreview);
  const [prizes, setPrizes] = useState([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [hasSpun, setHasSpun] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const wheelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAdminPreview) {
      setPrizes(previewSlots);
      return;
    }

    const checkStatus = async () => {
      const token = localStorage.getItem("token");
      setIsLoggedIn(!!token);

      try {
        console.log("🎡 Checking spin status...");
        const res = await api.get("/spin/status");

        console.log("🎡 Spin status response:", res.data);

        if (res.data.canSpin === false) {
          console.log("🎡 User ineligible to spin (backend).");
          setHasSpun(true);
          setShow(false);
        } else {
          const lastSpin = localStorage.getItem("lastSpinTime");
          const today = new Date().toDateString();
          console.log("🎡 Local storage spin check:", { lastSpin, today });

          if (lastSpin !== today) {
            console.log("🎡 Popup will show in 2s...");
            setTimeout(() => setShow(true), 2000);
          } else {
            console.log("🎡 User already spun today (localStorage).");
          }
        }
      } catch (err) {
        console.error("🎡 Error checking spin status:", err);
        // Fallback for guests if API fails
        if (!token) {
          const lastSpin = localStorage.getItem("lastSpinTime");
          if (lastSpin !== new Date().toDateString()) {
            setTimeout(() => setShow(true), 2000);
          }
        }
      }
    };

    checkStatus();
    fetchPrizes();
  }, []);

  const fetchPrizes = async () => {
    try {
      const res = await api.get("/wheel/slots");
      setPrizes(res.data.sort((a, b) => a.index - b.index));
    } catch (err) {
      console.error("Error fetching prizes:", err);
      // Fallback nếu API lỗi
      const mockPrizes = Array(10)
        .fill(0)
        .map((_, i) => ({
          label: i % 2 === 0 ? "MAY MẮN" : "GIẢM GIÁ",
          color: i % 2 === 0 ? "#EF4444" : "#000000",
        }));
      setPrizes(mockPrizes);
    }
  };

  const spin = async () => {
    if (isSpinning || (hasSpun && !isAdminPreview)) return;

    setIsSpinning(true);

    if (isAdminPreview) {
      // Logic quay giả lập cho Admin Test
      const random = Math.random();
      let cumulativeProbability = 0;
      let slotIndex = 0;

      for (let i = 0; i < prizes.length; i++) {
        cumulativeProbability += prizes[i].probability;
        if (random <= cumulativeProbability) {
          slotIndex = i;
          break;
        }
      }

      const segmentAngle = 360 / prizes.length;
      const extraRotation = 360 * 10;
      const targetRotation = extraRotation + (360 - slotIndex * segmentAngle);

      setRotation((prev) => prev + targetRotation);

      setTimeout(() => {
        setIsSpinning(false);
        const winSlot = prizes[slotIndex];
        setResult({ displayName: winSlot.label, isSpecial: true });
        toast.info(`🧪 TEST: Bạn đã trúng vào ô "${winSlot.label}"`);
      }, 6000);
      return;
    }

    try {
      const res = await api.post("/spin");

      const { slotIndex, label, voucher, message } = res.data;

      // Tính toán góc quay (quay ít nhất 10 vòng + góc tới prize)
      const segmentAngle = 360 / prizes.length;
      const extraRotation = 360 * 10; // 10 vòng cho mượt

      // Góc quay cần thiết để kim chỉ vào đúng ô (kim ở 12 giờ, góc 0)
      // Ô 0 ở góc 0, Ô 1 ở góc segmentAngle...
      // Để kim chỉ vào ô i, tâm ô đó phải ở góc 0.
      // Do vòng quay quay theo chiều kim đồng hồ, ta dùng (360 - i * segmentAngle)
      const targetRotation = extraRotation + (360 - slotIndex * segmentAngle);

      setRotation((prev) => prev + targetRotation);

      setTimeout(() => {
        setIsSpinning(false);
        // Ưu tiên hiển thị GiftName nếu là quà tặng, nếu không thì hiển thị Code của Voucher, cuối cùng là Label ô quay
        const prizeName = voucher
          ? voucher.type === "gift"
            ? voucher.giftName
            : voucher.code
          : label;

        setResult(
          voucher
            ? { ...voucher, displayName: prizeName }
            : { displayName: prizeName, isSpecial: true },
        );
        setHasSpun(true);
        localStorage.setItem("lastSpinTime", new Date().toDateString());

        if (voucher) {
          if (!isLoggedIn) {
            localStorage.setItem("wonVoucherId", voucher._id);
            toast.info(
              `🎟️ Bạn đã trúng: ${prizeName}! Đăng nhập ngay để nhận.`,
            );
          } else {
            toast.success(
              `🎉 Chúc mừng! ${prizeName} đã được lưu vào kho của bạn.`,
            );
          }
        } else {
          toast.info(`💫 Kết quả: ${label}`);
        }
      }, 6000);
    } catch (err) {
      setIsSpinning(false);
      toast.error(err.response?.data || "Có lỗi xảy ra khi quay");
    }
  };

  if (!show || (hasSpun && !result)) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/60 dark:bg-black/80 backdrop-blur-sm p-2 transition-colors duration-500 overflow-y-auto">
      <div className="relative bg-white dark:bg-slate-900 rounded-[24px] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] dark:shadow-blue-900/20 overflow-hidden max-w-4xl w-full flex flex-col md:flex-row min-h-[400px] md:min-h-[500px] animate-scale-in border border-gray-100 dark:border-slate-800 scale-[0.85] sm:scale-95 md:scale-100 origin-center">
        {/* Nút đóng */}
        <button
          onClick={() => (onPreviewClose ? onPreviewClose() : setShow(false))}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-all z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md shadow-sm border border-gray-100 dark:border-slate-700"
        >
          <MdClose
            size={20}
            className="text-gray-500 hover:text-gray-900 dark:hover:text-white"
          />
        </button>

        {/* Vùng Vòng Quay (Trái) với kích thước được mở rộng */}
        <div className="md:w-[45%] p-2 md:p-6 bg-[#F8FAFC] dark:bg-slate-800/50 relative flex items-center justify-center overflow-hidden border-r border-gray-50 dark:border-slate-800">
          <div
            className="absolute inset-0 opacity-[0.02] pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(#000 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
          ></div>

          <div className="relative z-10">
            {/* Kim chỉ vòng quay */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-30 drop-shadow-md">
              <div className="w-6 h-8 bg-red-600 clip-path-pointer border-t-2 border-x-2 border-white shadow-sm"></div>
            </div>

            {/* Vòng quay wrapper lớn hơn */}
            <div className="relative p-1.5 md:p-3 bg-white/60 rounded-full shadow-sm">
              <div
                className="w-[260px] h-[260px] md:w-80 md:h-80 rounded-full border-[6px] md:border-[8px] border-white shadow-[0_10px_30px_rgba(0,0,0,0.15)] relative overflow-hidden transition-transform duration-[6000ms] cubic-bezier(0.1, 0, 0.1, 1)"
                style={{ transform: `rotate(${rotation}deg)` }}
              >
                {prizes.map((prize, i) => {
                  const angle = 360 / prizes.length;
                  const colors = ["#B91C1C", "#EF4444", "#000000"]; // Red 700, Red 500, Black
                  // Calculate width of the segment top based on angle (2 * radius * tan(angle/2))
                  // Radius is 100%, so width is 2 * tan(angle/2) * 100%
                  const wedgeWidth =
                    Math.tan(((angle / 2) * Math.PI) / 180) * 100;

                  return (
                    <div
                      key={i}
                      className="absolute top-0 left-0 w-full h-full origin-center"
                      style={{ transform: `rotate(${i * angle}deg)` }}
                    >
                      <div
                        className="absolute top-0 left-1/2 -translate-x-1/2 h-1/2 flex items-start justify-center pt-5"
                        style={{
                          width: `${wedgeWidth}%`,
                          backgroundColor: prize.color || colors[i % 3],
                          clipPath: `polygon(50% 100%, 0 0, 100% 0)`,
                        }}
                      >
                        <div className="text-center px-1">
                          <span className="text-white font-black text-[10px] md:text-[12px] tracking-tight uppercase drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] whitespace-pre-wrap block leading-tight max-w-[50px] mx-auto">
                            {prize.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tâm vòng quay nhỏ gọn */}
              <div className="absolute inset-0 m-auto w-12 h-12 bg-white dark:bg-slate-900 rounded-full shadow-[0_5px_15px_rgba(0,0,0,0.15)] z-20 flex items-center justify-center border border-gray-100 dark:border-slate-800">
                <div className="w-9 h-9 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-inner p-1.5">
                  <img
                    src={logo}
                    alt="TaHa Logo"
                    className="w-full h-full object-contain filter drop-shadow-[0_0_3px_rgba(220,38,38,0.2)]"
                  />
                </div>
              </div>
            </div>

            {/* Indicators tinh tế hơn */}
            <div className="mt-6 flex justify-center items-center gap-2">
              <div className="w-8 h-1 bg-red-600 rounded-full opacity-40"></div>
              <div className="w-8 h-1 bg-gray-200 dark:bg-slate-700 rounded-full"></div>
              <div className="w-8 h-1 bg-red-600 rounded-full opacity-40"></div>
            </div>
          </div>
        </div>

        {/* Nội dung (Phải) */}
        <div className="md:w-[55%] p-4 md:p-12 flex flex-col justify-center bg-white dark:bg-slate-900">
          <div className="flex items-center gap-2.5 mb-4 md:mb-6">
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg flex items-center justify-center text-white">
              <img
                src={logo}
                alt=""
                className="w-full h-full object-contain border-[1px] rounded-full border-white"
              />
            </div>
            <span className="text-base md:text-lg font-black text-gray-900 dark:text-white tracking-tight">
              TaHa <span className="text-red-600">Shoes</span>
            </span>
          </div>

          <h2 className="text-xl md:text-4xl font-black text-gray-900 dark:text-white leading-[1.1] mb-2 md:mb-5 tracking-tighter">
            Quay Ngay, <br />
            <span className="text-red-600">Nhận Lộc</span> Khủng!
          </h2>

          <p className="text-gray-500 dark:text-slate-400 text-sm md:text-base leading-relaxed mb-3 md:mb-6 max-w-[320px]">
            Cơ hội nhận mã giảm giá giới hạn từ TaHa{" "}
            <span className="text-red-600">Shoes</span>. Quay ngay hôm nay!
          </p>

          {!result ? (
            <div className="space-y-3">
              {!isLoggedIn && (
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <span className="material-symbols-outlined text-[18px]">
                      mail
                    </span>
                  </div>
                  <input
                    type="email"
                    placeholder="Nhập email của bạn"
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 rounded-[12px] outline-none focus:border-red-600 focus:bg-white dark:focus:bg-slate-700 transition-all text-sm font-bold text-gray-900 dark:text-white"
                  />
                </div>
              )}

              <button
                onClick={spin}
                disabled={isSpinning}
                className={`w-full py-3 md:py-4 rounded-[12px] text-lg font-black text-white shadow-[0_10px_25px_-8px_rgba(220,38,38,0.4)] transition-all flex items-center justify-center gap-3 ${
                  isSpinning
                    ? "bg-gray-400 cursor-not-allowed scale-[0.98]"
                    : "bg-red-600 hover:bg-red-700 hover:translate-y-[-2px] active:translate-y-0"
                }`}
              >
                {isSpinning ? "ĐANG QUAY..." : "QUAY NGAY"}
                <span className="material-symbols-outlined text-[20px]">
                  rocket_launch
                </span>
              </button>
            </div>
          ) : (
            <div className="animate-fade-in bg-red-50/50 dark:bg-slate-800/50 border border-red-100 dark:border-slate-800 rounded-[20px] p-6 shadow-sm">
              <p className="text-red-600 font-black uppercase tracking-wider mb-1.5 text-[10px]">
                Chúc mừng!
              </p>
              <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-4 leading-tight">
                Bạn đã trúng:{" "}
                <span className="text-red-600">{result.displayName}</span>
              </h3>

              {!isLoggedIn ? (
                <div>
                  <p className="text-gray-600 dark:text-slate-400 mb-5 text-[13px] leading-relaxed font-semibold">
                    Hãy đăng nhập/đăng ký để nhận mã ngay!
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShow(false);
                        navigate(
                          `/login?redirect=${encodeURIComponent(window.location.pathname)}`,
                        );
                      }}
                      className="flex-1 py-3 bg-red-600 text-white rounded-[12px] font-black text-sm hover:bg-red-700 transition-all shadow-md shadow-red-100"
                    >
                      Đăng nhập
                    </button>
                    <button
                      onClick={() => {
                        setShow(false);
                        navigate(
                          `/register?redirect=${encodeURIComponent(window.location.pathname)}`,
                        );
                      }}
                      className="flex-1 py-3 bg-white text-red-600 border-2 border-red-600 rounded-[12px] font-black text-sm hover:bg-red-50 transition-all"
                    >
                      Đăng ký
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {!result.isSpecial ? (
                    <>
                      <p className="text-green-600 dark:text-green-400 mb-4 text-[13px] leading-relaxed font-bold flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">
                          check_circle
                        </span>
                        Voucher đã được tự động lưu vào kho!
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => {
                            setShow(false);
                            navigate("/vouchers");
                          }}
                          className="flex-1 py-3 bg-red-600 text-white rounded-[12px] font-black text-sm hover:bg-red-700 transition-all shadow-lg"
                        >
                          Xem kho voucher
                        </button>
                        <button
                          onClick={() => setShow(false)}
                          className="flex-1 py-3 bg-white text-gray-500 border-2 border-gray-100 rounded-[12px] font-black text-sm hover:bg-gray-50 transition-all"
                        >
                          Đóng
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-500 dark:text-slate-400 mb-4 text-[13px] leading-relaxed font-semibold">
                        Cảm ơn bạn đã tham gia! Chúc bạn may mắn lần sau với các
                        ưu đãi khác.
                      </p>
                      <button
                        onClick={() => setShow(false)}
                        className="w-full py-3 bg-red-600 text-white rounded-[12px] font-black text-base hover:bg-red-700 shadow-lg"
                      >
                        Đóng
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-8 flex items-center justify-between text-[9px] font-black text-gray-400 dark:text-slate-600 uppercase tracking-widest opacity-60">
            <span>
              TaHa <span className="text-red-500">Shoes</span> © 2026
            </span>
            <div className="flex gap-3">
              <span className="material-symbols-outlined text-[12px]">
                verified_user
              </span>
              <span className="material-symbols-outlined text-[12px]">
                lock
              </span>
            </div>
          </div>
        </div>

        <style>{`
          .clip-path-pointer {
            clip-path: polygon(0% 0%, 100% 0%, 50% 100%);
          }
          @keyframes scale-in {
            from {
              transform: scale(0.98);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
          @keyframes fade-in {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-scale-in {
            animation: scale-in 0.4s ease-out;
          }
          .animate-fade-in {
            animation: fade-in 0.4s ease-out;
          }
        `}</style>
      </div>
    </div>
  );
};

export default LuckyWheel;
